import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field } from "react-final-form";
import { useEffect, useRef } from "react";
import { useFormState, useForm } from "react-final-form";

import BMXSelectField from "../../../../../../Components/Inputs/BMXSelectField/BMXSelectField";
import BMXTextField from "../../../../../../Components/Inputs/BMXTextField/BMXTextField";
import { getTypeOfSettlementTransportService } from "../../../../../../Utils/GlobalVariables/Service/BillingType";
import { useDistanceHook } from "../../../../../../Hooks/GoogleHook";

import "./ServiceTransportModalFormStepOne.scss";

const ServiceTransportModalFormStepOne = ({ initialValues }) => {
  const { t } = useTranslation();

  const typeOfSettlementTransportService =
    getTypeOfSettlementTransportService(t);

  const { distance } = useDistanceHook({
    to: `${initialValues?.device?.branch?.address} ${initialValues?.device?.branch?.city}`,
  });

  const { values } = useFormState({
    subscription: { values: true },
  });

  const form = useForm();

  const settlementType =
    values?.typeOfSettlementTransportService?.value;

  const settleTheReturnTransportService =
    values?.settleTheReturnTransportService;

  const SETTLEMENT = {
    AUTO: "auto",
    MANUAL: "manual",
    COUNTER: "counter",
    CONSTANT: "constant",
  };

  const prevSettlementType = useRef();

  const clearTransportFields = () => {
    [
      "autoTransportService",
      "counterTransportService",
      "manuallyTransportService",
    ].forEach((field) => form.change(field, undefined));
  };

  const round2 = (val) => Math.round(val * 100) / 100;

  const withReturnMultiplier = (value) => {
    const normalized = round2(value);
    return settleTheReturnTransportService
      ? round2(normalized * 2)
      : normalized;
  };

  // ✅ GŁÓWNA LOGIKA - poprawiona
  useEffect(() => {
    if (!settlementType) return;

    const isTypeChanged =
      prevSettlementType.current &&
      prevSettlementType.current !== settlementType;

    const baseDistance = distance?.value
      ? distance.value / 1000
      : null;

    const initial = Number(initialValues?.initialCounter);
    const final = Number(initialValues?.finalCounter);

    const calculations = {
      [SETTLEMENT.AUTO]: () => {
        if (baseDistance == null) return undefined;
        return withReturnMultiplier(baseDistance);
      },
      [SETTLEMENT.COUNTER]: () => {
        if (isNaN(initial) || isNaN(final)) return undefined;
        return withReturnMultiplier(final - initial);
      },
      [SETTLEMENT.MANUAL]: () => undefined,
    };

    const fieldMap = {
      [SETTLEMENT.AUTO]: "autoTransportService",
      [SETTLEMENT.COUNTER]: "counterTransportService",
      [SETTLEMENT.MANUAL]: "manuallyTransportService",
    };

    // ✅ czyścimy tylko jeśli user zmienił typ
    if (isTypeChanged) {
      clearTransportFields();
    }

    // manual – nie nadpisujemy wartości z BE
    if (settlementType === SETTLEMENT.MANUAL) {
      form.change("isStaticRateTransportPriceService", false);
      form.change("settleTheReturnTransportService", false);
      prevSettlementType.current = settlementType;
      return;
    }

    const value = calculations[settlementType]?.();

    if (fieldMap[settlementType]) {
      form.change(fieldMap[settlementType], value);
    }

    prevSettlementType.current = settlementType;

    // eslint-disable-next-line
  }, [
    settlementType,
    distance?.value,
    initialValues?.initialCounter,
    initialValues?.finalCounter,
    settleTheReturnTransportService,
    form,
  ]);

  // billing type
  useEffect(() => {
    const billingType =
      settlementType === SETTLEMENT.CONSTANT
        ? {
          value: "constant",
          name: t("global:servicevaluation.constant"),
        }
        : {
          value: "counter",
          name: t("global:servicevaluation.rateOneKm"),
        };

    form.change("billingTypeTransportService", billingType);
    // eslint-disable-next-line
  }, [settlementType, t, form]);

  // koszt transportu
  useEffect(() => {
    const staticPrice =
      initialValues?.device?.printerModel?.servicePrice
        ?.staticRateTransportPrice;

    const ratePerKm =
      initialValues?.device?.printerModel?.servicePrice
        ?.rateOneKmTransportPrice;

    if (settlementType === SETTLEMENT.CONSTANT) {
      form.change(
        "transportCostTransportService",
        staticPrice ?? undefined
      );
      return;
    }

    if (!ratePerKm) {
      form.change("transportCostTransportService", undefined);
      return;
    }

    const baseValueArr = {
      [SETTLEMENT.AUTO]: values?.autoTransportService,
      [SETTLEMENT.COUNTER]: values?.counterTransportService,
      [SETTLEMENT.MANUAL]: values?.manuallyTransportService,
    };

    const baseValue = baseValueArr[settlementType];

    if (baseValue == null || baseValue === "") {
      form.change("transportCostTransportService", undefined);
      return;
    }

    form.change(
      "transportCostTransportService",
      round2(baseValue * ratePerKm)
    );

    // eslint-disable-next-line
  }, [settlementType, values, initialValues, form]);

  // default settlement type
  useEffect(() => {
    if (values?.typeOfSettlementTransportService) return;

    const defaultOption =
      typeOfSettlementTransportService.find(
        (opt) => opt.value === SETTLEMENT.AUTO
      );

    if (defaultOption) {
      form.change(
        "typeOfSettlementTransportService",
        defaultOption
      );
    }

    // eslint-disable-next-line
  }, [
    values?.typeOfSettlementTransportService,
    typeOfSettlementTransportService,
    form,
  ]);

  return (
    <div
      className="ServiceTransportModalFormStepOne"
      style={{ justifyContent: "flex-start" }}
    >
      <Row>
        <Col xs={{ span: 8, offset: 2 }}>
          <Field
            name="typeOfSettlementTransportService"
            values={typeOfSettlementTransportService}
            component={BMXSelectField}
            label={t(
              "global:servicevaluation.typeOfSettlementTransportService"
            )}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={{ span: 8, offset: 2 }}>
          <Field
            name="settleTheReturnTransportService"
            fieldtype="checkbox"
            type="checkbox"
            disabled={settlementType === "manual"}
            component={BMXTextField}
            label={t(
              "global:servicevaluation.settleTheReturnTransportService"
            )}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={{ span: 8, offset: 2 }}>
          <Field
            name="isStaticRateTransportPriceService"
            fieldtype="checkbox"
            type="checkbox"
            disabled={settlementType === "manual"}
            component={BMXTextField}
            label={t(
              "global:servicevaluation.isStaticRateTransportPriceService"
            )}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col
          className={settlementType === "auto" ? "active" : ""}
          xs={{ span: 2, offset: 2 }}
        >
          <Field
            name="autoTransportService"
            disabled
            component={BMXTextField}
            label={t(
              "global:servicevaluation.autoTransportService"
            )}
          />
        </Col>

        <Col
          xs={2}
          className={settlementType === "counter" ? "active" : ""}
        >
          <Field
            disabled
            name="counterTransportService"
            component={BMXTextField}
            label={t(
              "global:servicevaluation.counterTransportService"
            )}
          />
        </Col>

        <Col
          xs={2}
          className={settlementType === "manual" ? "active" : ""}
        >
          <Field
            name="manuallyTransportService"
            disabled={settlementType !== "manual"}
            component={BMXTextField}
            label={t(
              "global:servicevaluation.manuallyTransportService"
            )}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={{ span: 4, offset: 2 }}>
          <Field
            name="billingTypeTransportService.name"
            readOnly
            component={BMXTextField}
            label={t(
              "global:servicevaluation.billingTypeTransportService"
            )}
          />
        </Col>

        <Col xs={4}>
          <Field
            name="transportCostTransportService"
            type="money"
            decimalScale={2}
            component={BMXTextField}
            label={t(
              "global:servicevaluation.transportCostTransportService"
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ServiceTransportModalFormStepOne;